import axios from 'axios';
import { toast } from 'react-hot-toast';

// API Gateway - single entry point for all services
const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL || 'https://salon-api-gateway-351441996042.us-central1.run.app/api/v1';

const api = axios.create({
  baseURL: API_GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're already refreshing to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to refresh the access token
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'));
  }

  try {
    const response = await axios.post(
      `${API_GATEWAY_URL}/auth/refresh`,
      { refreshToken }
    );
    const { accessToken, refreshToken: newRefreshToken } = response.data;
    localStorage.setItem('token', accessToken);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    return accessToken;
  } catch (error) {
    // Refresh failed, clear tokens and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
    return Promise.reject(error);
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Token added to request:', config.url);
    } else {
      console.warn('No token found in localStorage for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        refreshAccessToken()
          .then((token) => {
            processQueue(null, token);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // Skip toast if the caller opted out
    const silent = error.config?.silent;

    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        if (!silent) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
        }
      } else if (status === 403) {
        if (!silent) toast.error('You do not have permission to perform this action.');
      } else if (status === 404) {
        if (!silent) toast.error('Resource not found.');
      } else if (status >= 500) {
        if (!silent) toast.error('Server error. Please try again later.');
      } else {
        if (!silent) toast.error(data.error || data.message || 'An error occurred.');
      }
    } else if (error.request) {
      if (!silent) toast.error('Network error. Please check your connection.');
    } else {
      if (!silent) toast.error('An unexpected error occurred.');
    }
    
    return Promise.reject(error);
  }
);

export default api;